import { graphql } from "@/config/graphql";
import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const Content = styled(Box)`
  padding: 16px 24px;
`;
const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const addBatchControlDetailMutation = graphql(`
  mutation addBatchControlDetail($params: AddBatchControlDetailParams!) {
    addBatchControlDetail(params: $params) {
      id
      batchControlDetails {
        id
        initialHourometer
        finalHourometer
        waste
      }
    }
  }
`);
const updateBatchControlDetailMutation = graphql(`
  mutation updateBatchControlDetail($params: UpdateBatchControlDetailParams!) {
    updateBatchControlDetail(params: $params) {
      id
      batchControlDetails {
        id
        initialHourometer
        finalHourometer
        waste
      }
    }
  }
`);
const removeBatchControlDetailMutation = graphql(`
  mutation removeBatchControlDetail($params: RemoveBatchControlDetailParams!) {
    removeBatchControlDetail(params: $params) {
      id
      batchControlDetails {
        id
        initialHourometer
        finalHourometer
        waste
      }
    }
  }
`);

const productionDetailSchema = z
  .object({
    waste: z.coerce
      .number({
        required_error: "Ingrse la cantidad rechazada",
        invalid_type_error: "Ingrse la cantidad rechazada",
      })
      .int()
      .gte(0, "Ingrese la cantidad rechazada"),
    initialHourometer: z.coerce
      .number({
        required_error: "Ingrese el horómetro inicial",
        invalid_type_error: "Ingrese el horómetro inicial",
      })
      .int()
      .gte(0, "Ingrese el horómetro inicial"),
    finalHourometer: z.coerce
      .number({
        required_error: "Ingrese el horómetro final",
        invalid_type_error: "Ingrese el horómetro final",
      })
      .int()
      .gte(0, "Ingrese el horómetro final"),
    id: z.number().optional(),
  })
  .refine(
    (data) => {
      if (data.finalHourometer - data.initialHourometer < 0) {
        return false;
      }
      return true;
    },
    {
      message: "El horómetro final debe ser mayor al inicial",
      path: ["finalHourometer"],
    }
  );
type BatchControlDetailSchema = z.infer<typeof productionDetailSchema>;
interface BatchControlReportFormProps {
  close: () => void;
  defaultValues?: Partial<BatchControlDetailSchema>;
}
export const BatchControlReportForm = (props: BatchControlReportFormProps) => {
  const params = useParams();
  const { control, handleSubmit, watch } = useForm<BatchControlDetailSchema>({
    resolver: zodResolver(productionDetailSchema),
    defaultValues: props.defaultValues,
  });
  const [addBatchControlDetail, { loading: adding }] = useMutation(
    addBatchControlDetailMutation
  );
  const [updateBatchControlDetail, { loading: updating }] = useMutation(
    updateBatchControlDetailMutation
  );
  const [removeBatchControlDetail, { loading: removing }] = useMutation(
    removeBatchControlDetailMutation,
    { variables: { params: { id: props.defaultValues?.id || 0 } } }
  );

  const [initialHourometer, finalHourometer] = watch([
    "initialHourometer",
    "finalHourometer",
  ]);

  const handleClose = () => props.close();
  const handleRemove = async () => {
    await removeBatchControlDetail();
    props.close();
  };
  const onSubmit = async (data: BatchControlDetailSchema) => {
    if (props.defaultValues?.id) {
      await updateBatchControlDetail({
        variables: {
          params: {
            id: props.defaultValues.id,
            ...data,
          },
        },
      });
    } else {
      await addBatchControlDetail({
        variables: {
          params: {
            ...data,
            reportId: +(params.id || 0),
          },
        },
      });
    }
    handleClose();
  };
  return (
    <>
      <Content>
        <Typography variant="h6">Agregar Partida</Typography>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="initialHourometer"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Horómetro inicial"
                fullWidth
                type="number"
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                {...field}
                value={field.value || ""}
              ></TextField>
            )}
          />

          <Controller
            name="finalHourometer"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <TextField
                  label="Horómetro final"
                  fullWidth
                  type="number"
                  onWheel={() =>
                    (document.activeElement as HTMLInputElement)?.blur()
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...field}
                  value={field.value || ""}
                ></TextField>

                <Typography
                  fontWeight={800}
                  color={fieldState.error ? "error" : "inherit"}
                >
                  Horas:{" "}
                  <Typography component="span" display={"inline"}>
                    {finalHourometer !== undefined &&
                    initialHourometer !== undefined
                      ? finalHourometer - initialHourometer
                      : "Por calcular"}
                  </Typography>
                </Typography>
              </>
            )}
          />

          <Controller
            name="waste"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Pérdida"
                fullWidth
                type="number"
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: <Typography>ton</Typography>,
                }}
                {...field}
                value={field.value || ""}
              ></TextField>
            )}
          />
        </Form>
      </Content>
      <DialogActions sx={{ padding: "8px 24px" }}>
        {props.defaultValues?.id ? (
          <>
            <Button
              onClick={handleRemove}
              color="error"
              disabled={removing || updating}
            >
              ELIMINAR
              {removing && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} />
              )}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="info"
              disabled={updating || removing}
            >
              GUARDAR
              {updating && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} />
              )}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} color="error">
              CANCELAR
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="info"
              disabled={adding}
            >
              GUARDAR
              {adding && <CircularProgress size={20} sx={{ marginLeft: 1 }} />}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
};
